import { fetchBookingsForRoom } from "../../api";
import { queryKeys } from "../../api";
import { getBookingTimeConstraint } from "../utils/bookingTimeConstraints.js";
import { generateBookingTitle } from "../utils/bookingTitleUtils.js";
import { useUrlFilterPersistence } from "../composables/useUrlFilterPersistence.js";
import { syncTimeGridScrollers } from "../utils/scrollSyncUtils.js";
import { evaluateCalendarSelection } from "../utils/walkInSelection.js";
import { createOpeningHoursIndex } from "../utils/openingHours.js";

// FullCalendar inverts "inverse-background" events per groupId: all events sharing a groupId
// are inverted together, while events without one are inverted individually (which would shade
// every session except itself). All opening-hours sessions of this single room therefore have
// to share one group. This view has exactly one room, so a fixed key is enough - the multi-room
// view uses the room id instead, because it needs one group per resource.
const OPENING_HOURS_GROUP_ID = "opening-hours";

export function useSingleRoomConfig(
	props,
	queryClient,
	masterData,
	handlers = {},
	calendarFilters = null,
) {
    // Check if mobile/small screen
    const isMobile = window.innerWidth <= 768;

    // Get current user role for time constraints
    const userRole = masterData?.userRole?.value || null;
    const earliestBookingSeconds = masterData?.earliestBookingSeconds?.value || 900;
    const canIgnoreOpeningHours =
        masterData?.canIgnoreOpeningHours?.value || false;

    // Opening hours of the loaded date range, indexed while the background events below are
    // built - used by selectAllow to refuse drags into closed times
    const openingHoursIndex = createOpeningHoursIndex();

    // View mapping for shorter URL params
    const paramToView = {
        d: "timeGridDay",
        w: "timeGridWeek",
        m: "dayGridMonth",
    };

    // Get view and date from URL params or use defaults
    const urlParams = new URLSearchParams(window.location.search);
    const savedViewParam = urlParams.get("view");
    const savedView = savedViewParam ? paramToView[savedViewParam] : null;
    const defaultView = isMobile ? "timeGridDay" : "timeGridWeek";
    const initialView = savedView || defaultView;

    // Get saved date from URL
    const savedDate = urlParams.get("date");

    // URL persistence for date only
    const urlPersistence = useUrlFilterPersistence();

    return {
        height: "70vh",
        initialView,
        initialDate: savedDate || undefined,

        headerToolbar: isMobile
            ? {
                left: "",
                center: "title",
                right:
                    "prev,next,today,datePicker,timeGridDay,timeGridWeek,dayGridMonth",
            }
            : {
                left: "title",
                center: "",
                right:
                    "prev,next,today,datePicker,timeGridDay,timeGridWeek,dayGridMonth",
            },

        // Room-specific selectable behavior - enabled if either regular or walk-in
        // permission applies; selectAllow below does the precise, time-aware gating
        selectable:
            (masterData?.canCreateInRoom?.value(props.roomId) ?? false) ||
            (masterData?.canCreateWalkInInRoom?.value(props.roomId) ?? false),

        // Time-aware selection: block past slots; Ad-Hoc only in the near window
        selectAllow: function (selectInfo) {
            const { allowed, isWalkIn } = evaluateCalendarSelection({
                selectStart: selectInfo.start,
                selectEnd: selectInfo.end,
                userRole,
                earliestBookingSeconds,
            });
            if (!allowed) return false;

            // Without the "ignore opening hours" permission a booking outside the opening
            // hours is impossible (walk-ins can never ignore them at all), so refuse the drag
            // instead of opening a modal that can only end in a server-side error
            if (
                !canIgnoreOpeningHours &&
                !openingHoursIndex.isRangeOpen(
                    props.roomId,
                    selectInfo.start,
                    selectInfo.end,
                )
            ) {
                return false;
            }

            return isWalkIn
                ? masterData?.canCreateWalkInInRoom?.value(props.roomId) ?? false
                : masterData?.canCreateInRoom?.value(props.roomId) ?? false;
        },

        // Show all-day slot for holidays
        allDaySlot: true,
        allDayText: "FT",
        allDayDidMount: function (info) {
            info.el.setAttribute("title", "Feiertag");
        },

        // Mobile touch optimizations with dynamic scroll time
        scrollTime: (() => {
            const now = new Date();
            const currentHour = now.getHours();
            const currentMinute = now.getMinutes();

            return `${String(currentHour).padStart(2, "0")}:${String(
                currentMinute,
            ).padStart(2, "0")}:00`;
        })(),
        scrollTimeReset: false,

        // Mobile-specific view settings
        slotMinWidth: 20,
        slotDuration: isMobile ? "00:30:00" : "00:15:00",
        slotLabelInterval: isMobile ? "02:00:00" : "01:00:00",

        // Events for single room booking (+ optional ConcertRequest overlay)
        events: async function (fetchInfo, successCallback, failureCallback) {
            try {
                // Build date range from fetchInfo
                const dateRange = {
                    start: fetchInfo.start.toISOString().split("T")[0],
                    end: fetchInfo.end.toISOString().split("T")[0],
                };

                // Fetch bookings using queryClient
                const result = await queryClient.fetchQuery({
                    queryKey: queryKeys.bookingsByRoom(props.roomId, dateRange),
                    queryFn: () => fetchBookingsForRoom(props.roomId, dateRange),
                    staleTime: 0, // Force fresh fetch when refetchEvents() is called
                });

                // Transform bookings to FullCalendar events
                const bookingEvents = result.bookings.map((booking) => {
                    // Apply time constraints only for students who actually have base permissions
                    const timeConstraint =
                        userRole === "student" &&
                        (booking.permissions.canUpdate || booking.permissions.canDelete)
                            ? getBookingTimeConstraint(booking, userRole)
                            : { restricted: false };

                    const effectiveCanEdit =
                        booking.permissions.canUpdate && !timeConstraint.restricted;
                    const effectiveCanDelete =
                        booking.permissions.canDelete && !timeConstraint.restricted;

                    // Add visual indicator classes for time-restricted bookings
                    const classNames = booking.series ? ["series-event"] : [];
                    if (timeConstraint.restricted && userRole === "student") {
                        classNames.push("time-restricted-booking");
                    }

                    // Determine ownership by comparing user IDs
                    const currentUserInfo = masterData?.masterData?.value?.userInfo;
                    let isOwnBooking = false;

                    if (currentUserInfo) {
                        // Check employee ownership
                        if (
                            currentUserInfo.employee &&
                            booking.employee &&
                            currentUserInfo.employee.id === booking.employee.id
                        ) {
                            isOwnBooking = true;
                        }
                        // Check student ownership
                        if (
                            currentUserInfo.student &&
                            booking.student &&
                            currentUserInfo.student.id === booking.student.id
                        ) {
                            isOwnBooking = true;
                        }
                    }

                    // Add ownership and permission classes
                    if (isOwnBooking) {
                        classNames.push("owned-booking");
                        if (effectiveCanEdit) {
                            classNames.push("can-edit");
                        }
                    }

                    // Get booking type details from master data
                    const bookingType = masterData?.getBookingTypeById?.value(
                        booking.bookingTypeId,
                    );

                    // Permission to see booking details
                    const canViewOtherBookings = masterData?.canViewOtherBookings.value;

                    // Generate the title based on business requirements
                    const displayTitle = generateBookingTitle(
                        booking,
                        bookingType,
                        isOwnBooking || canViewOtherBookings,
                    );

					return {
						id: booking.id.toString(),
						title: displayTitle,
						start: booking.startTime,
						end: booking.endTime,
						backgroundColor: bookingType?.backgroundColor || "#007bff",
						borderColor: bookingType?.backgroundColor || "#007bff",
						textColor: bookingType?.textColor || "#ffffff",
						classNames,
						editable: effectiveCanEdit,
						startEditable: effectiveCanEdit,
						durationEditable: effectiveCanEdit,
						resourceId: booking.roomId.toString(),
						extendedProps: {
							booking,
							// Original permissions
							originalCanEdit: booking.permissions.canUpdate,
							originalCanDelete: booking.permissions.canDelete,
							// Effective permissions with time constraints
							canEdit: effectiveCanEdit,
							canDelete: effectiveCanDelete,
							// Time constraint information
							timeConstraint,
							seriesId: booking.seriesId || null,
							// Add booking filter data
							bookingTypeId: booking.bookingTypeId,
							studentId: booking.student?.id,
							employeeId: booking.employee?.id,
							bookingUserId: booking.bookingUserId || booking.bookingUser?.id,
						},
					};
				});

				const filteredBookingEvents = calendarFilters
					? calendarFilters.applyBookingFilters(bookingEvents)
					: bookingEvents;

                // Create resource-specific opening hours events using inverse-background
                const openingHoursEvents = [];

                openingHoursIndex.reset();

                // For each resource, create inverse-background events for opening hours
                Object.entries(result.openingHours).forEach(([date, dayData]) => {
                    // Add each session as an opening hours period with inverse-background
                    dayData.sessions.forEach((session) => {
                        openingHoursIndex.addSession(
                            props.roomId,
                            session.startTime,
                            session.endTime,
                        );
                        openingHoursEvents.push({
                            start: session.startTime,
                            end: session.endTime,
                            display: "inverse-background",
                            groupId: OPENING_HOURS_GROUP_ID,
                            backgroundColor: "#cccccc",
                        });
                    });

                    // if we have no sessions this would mean that the room is closed all day
                    if (!dayData.sessions?.length) {
                        openingHoursIndex.markLoaded(props.roomId);
                        openingHoursEvents.push({
                            start: new Date(date + "T00:00:00"),
                            end: new Date(date + "T23:59:59"),
                            display: "background",
                            groupId: OPENING_HOURS_GROUP_ID,
                            backgroundColor: "#cccccc",
                        });
                    }
                });

                // Create holiday all-day events
                const holidayEvents = Object.entries(result.holidays || {}).map(
                    ([date, name]) => ({
                        id: `holiday-${date}`,
                        title: name,
                        start: date,
                        allDay: true,
                        display: "block",
                        classNames: ["holiday-event"],
                        editable: false,
                        startEditable: false,
                        durationEditable: false,
                        backgroundColor: "#2d8a7b",
                        borderColor: "#257a6c",
                        textColor: "#ffffff",
                    }),
                );

				// Combine booking events, opening hours, and holidays
				const allEvents = [
					...filteredBookingEvents,
					...openingHoursEvents,
					...holidayEvents,
				];

                // Optional overlay: ConcertRequests
                let overlayEvents = [];
                if (props.overlayConcertRequestsApiUrl) {
                    const url = new URL(props.overlayConcertRequestsApiUrl, window.location.origin);
                    url.searchParams.set("start", dateRange.start);
                    url.searchParams.set("end", dateRange.end);

                    const response = await fetch(url.toString(), {
                        credentials: "same-origin",
                        headers: { Accept: "application/json" },
                    });

                    if (response.ok) {
                        const data = await response.json();
                        overlayEvents = data.events || [];
                    } else {
                        console.warn("ConcertRequest overlay API failed:", response.status);
                    }
                }

                successCallback([
                    ...allEvents,
                    ...overlayEvents,
                ]);
            } catch (error) {
                console.error("Failed to fetch calendar events:", error);
                failureCallback(error);
            }
        },

		// Update URL when view changes
		viewDidMount: function (info) {
			handlers.handleViewDidMount?.(info);

			// WebKit scroll sync fix: Safari has a race condition where header/body
			// scrollers desync during initial scrollTime application
			const fcEl = info.el.closest(".fc");
			if (fcEl) {
				requestAnimationFrame(() => {
					syncTimeGridScrollers(fcEl);
				});
			}

            urlPersistence.saveViewToUrl(info.view.type);

            const calendarEl = info.el;
            calendarEl.addEventListener("click", function (e) {
                const clickableHeader = e.target.closest(
                    ".fc-day-header-clickable, .fc-daygrid-day-number-clickable",
                );
                if (clickableHeader) {
                    e.preventDefault();
                    const dateStr = clickableHeader.getAttribute("data-date");
                    if (dateStr) {
                        info.view.calendar.changeView("timeGridDay", dateStr);
                    }
                }
            });
        },

        // Also update URL when user switches views or dates
        datesSet: function (info) {
            urlPersistence.saveViewToUrl(info.view.type);

            // Use the calendar's current date instead of info.start to avoid off-by-one errors
            const calendar = info.view.calendar;
            const currentDate = calendar.getDate();
            urlPersistence.saveDateToUrl(currentDate);
        },

        // Date click for creating events
        dateClick: function (dateClickInfo) {
            const roomId = props.roomId;
            if (!masterData?.canCreateInRoom?.value(roomId)) return;

            let startTime, endTime;

            if (dateClickInfo.view.type === "dayGridMonth") {
                // Month view: create booking with default times
                const clickedDate = dateClickInfo.date;
                const now = new Date();

                startTime = new Date(clickedDate);
                const currentHour = now.getHours();
                const defaultHour = currentHour < 9 ? 9 : currentHour;
                startTime.setHours(defaultHour, 0, 0, 0);

                // Set end time to 1 hour later
                endTime = new Date(startTime);
                endTime.setHours(startTime.getHours() + 1);
            } else {
                // Week/Day views: no instant tap creation, use selection (long press) only
                return;
            }

            // Create a mock selectInfo object that matches handleDateSelect expectations
            const mockSelectInfo = {
                start: startTime,
                end: endTime,
                view: dateClickInfo.view,
                resource: null, // Single room doesn't use resources
            };

            // Call the date select handler
            if (handlers.handleDateSelect) {
                handlers.handleDateSelect(mockSelectInfo);
            }
        },
    };
}
